import { alpha } from "@mui/material/styles";
import type { Theme, ThemeOptions } from "@mui/material/styles";
import { fieldErrorTextSx } from "@/shared/ui/fieldErrorText";
import { pageAlertSx } from "@/shared/ui/systemBannerStyles";
import { APP_BODY_FONT_SIZE_PX } from "./typography";
// The date pickers theme augmentation the styleOverrides below rely on lives in
// ./mui-augmentation.d.ts, kept type-only so it stays out of the entry chunk.
import type { HeaderSearchLayout } from "./theme";

/**
 * Structural theme base shared by every theme (built-in and future API-loaded):
 * typography, shape, and component overrides that define sizes and layout.
 * Colors are NOT defined here — component overrides read them from the active
 * theme's `theme.app` / palette tokens, so the same base works for all themes.
 */

const SHARED_TYPOGRAPHY: ThemeOptions["typography"] = {
  htmlFontSize: 16,
  fontSize: 16,
  fontFamily: "var(--font-sans), Arial, Helvetica, sans-serif",
  h1: { fontWeight: 700, fontSize: "3rem" },
};

/** Input heights by `size` prop: small = compact (filters), medium = forms (default). */
const INPUT_HEIGHTS = { small: 28, medium: 36 } as const;

/** Unset size resolves to "medium" (MUI FormControl default), i.e. 36px. */
const inputHeight = (size: string | undefined) =>
  size === "small" ? INPUT_HEIGHTS.small : INPUT_HEIGHTS.medium;

const outlinedInputRoot = ({ theme }: { theme: Theme }) => ({
  borderRadius: 8,
  backgroundColor: theme.app.filter.inputBackgroundColor,
});

const outlinedInputInput = ({ theme, size }: { theme: Theme; size: string | undefined }) => ({
  height: inputHeight(size),
  boxSizing: "border-box" as const,
  padding: "0 12px",
  fontSize: 14,
  color: theme.app.filter.inputTextColor,
  "&::placeholder": {
    color: theme.app.filter.inputPlaceholderColor,
    opacity: 1,
  },
  // The up/down glyphs are a browser control. Without this, dark fields keep a
  // light spinner from the OS theme.
  ...(theme.palette.mode === "dark" ? { '&[type="number"]': { colorScheme: "dark" } } : {}),
  // MUI Select pins `height: auto` on its display element via a
  // higher-specificity `&.MuiSelect-select` rule ("win specificity over the
  // input base"), so the plain height above doesn't reach selects. Match the
  // selector; theme overrides come later in the cascade and win. lineHeight
  // centers the text while preserving text-overflow ellipsis; paddingRight
  // for the dropdown icon stays with MUI's own `&&&` rule.
  "&.MuiSelect-select": {
    height: inputHeight(size),
    minHeight: inputHeight(size),
    lineHeight: `${inputHeight(size)}px`,
    boxSizing: "border-box" as const,
  },
});

/** `theme.shape.borderRadius` multiplier of an Advanced Search group card; list rows inside it round to the same corner. */
export const FILTER_GROUP_FRAME_RADIUS = 1.5;
/** Header strip height; matches the Sector group headers. */
const FILTER_GROUP_HEADER_HEIGHT_PX = 34;
const FILTER_GROUP_BORDER_PX = 1;

/** Matches the Data Grid's default scrollbar gutter so themed thumbs don't change grid layout. */
const SCROLLBAR_SIZE_PX = 10;

/**
 * Viewport and nested scrollbars (vertical and horizontal) follow the active theme.
 * The track stays clear so the same thumb works on page, dialog, and grid surfaces.
 * `scrollbar-color` inherits; WebKit pseudos do not, so those target every element.
 */
const scrollbarBaseline = (theme: Theme) => {
  const dark = theme.palette.mode === "dark";
  // Opacities clear 3:1 against paper and the lightest grid surface.
  const thumb = alpha(theme.app.dataGrid.secondaryTextColor, dark ? 0.7 : 0.85);
  const thumbHover = alpha(theme.app.dataGrid.secondaryTextColor, dark ? 0.85 : 1);
  return {
    html: {
      colorScheme: theme.palette.mode,
    },
    "*": {
      scrollbarWidth: "thin" as const,
      scrollbarColor: `${thumb} transparent`,
    },
    "*::-webkit-scrollbar": {
      width: SCROLLBAR_SIZE_PX,
      height: SCROLLBAR_SIZE_PX,
    },
    "*::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "*::-webkit-scrollbar-thumb": {
      backgroundColor: thumb,
      borderRadius: SCROLLBAR_SIZE_PX,
      border: "2px solid transparent",
      backgroundClip: "padding-box" as const,
    },
    "*::-webkit-scrollbar-thumb:hover": {
      backgroundColor: thumbHover,
    },
    "*::-webkit-scrollbar-corner": {
      backgroundColor: "transparent",
    },
  };
};

const SHARED_COMPONENTS: ThemeOptions["components"] = {
  MuiCssBaseline: {
    styleOverrides: scrollbarBaseline,
  },
  MuiPaper: {
    variants: [
      {
        props: { variant: "filterGroup" },
        style: ({ theme }) => ({
          backgroundColor: "transparent",
          backgroundImage: "none",
          boxShadow: "none",
          color: "inherit",
          border: `${FILTER_GROUP_BORDER_PX}px solid ${theme.app.dialog.footerBorderColor}`,
          borderRadius: Number(theme.shape.borderRadius) * FILTER_GROUP_FRAME_RADIUS,
          // Not `overflow: hidden` — that clips the selected Exclude toggle's drop shadow.
        }),
      },
      {
        props: { variant: "filterGroupHeader" },
        style: ({ theme }) => {
          const innerRadius = Math.max(
            Number(theme.shape.borderRadius) * FILTER_GROUP_FRAME_RADIUS - FILTER_GROUP_BORDER_PX,
            0,
          );
          return {
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            height: FILTER_GROUP_HEADER_HEIGHT_PX,
            minHeight: FILTER_GROUP_HEADER_HEIGHT_PX,
            maxHeight: FILTER_GROUP_HEADER_HEIGHT_PX,
            paddingInline: theme.spacing(1.5),
            boxShadow: "none",
            backgroundImage: "none",
            color: "inherit",
            borderBottom: `${FILTER_GROUP_BORDER_PX}px solid ${theme.app.dialog.footerBorderColor}`,
            backgroundColor: theme.app.dialog.footerBackground,
            // The first header follows the card's inner top corners; a header further down a card is square.
            borderRadius: `${innerRadius}px ${innerRadius}px 0 0`,
            "&:not(:first-child)": {
              borderRadius: 0,
              borderTop: `${FILTER_GROUP_BORDER_PX}px solid ${theme.app.dialog.footerBorderColor}`,
            },
          };
        },
      },
    ],
  },
  MuiTypography: {
    variants: [
      {
        props: { variant: "filterGroupTitle" },
        style: ({ theme }) => ({
          fontFamily: "var(--font-sans)",
          fontStyle: "normal",
          fontWeight: 700,
          fontSize: 9.75,
          lineHeight: "16px",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: theme.app.dialog.sectionTitleColor,
        }),
      },
    ],
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily: "var(--font-sans)",
        fontSize: `${APP_BODY_FONT_SIZE_PX}px`,
        "& *": {
          fontFamily: "inherit",
          fontSize: "inherit",
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { textTransform: "none" },
    },
  },
  // Status-banner chrome: type, 16px icon, 8px gap, 24px Close. Severity colors
  // stay on the Alert palette. Toasts pass their own sx and keep a 15px glyph.
  MuiAlert: {
    styleOverrides: {
      root: pageAlertSx,
    },
  },
  // Error copy under inputs. Hint text keeps the caption defaults; only Mui-error
  // picks up the alert face at regular weight. Call sites that set their own
  // fontSize on the helper still have to opt in, because sx beats this override.
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        "&.Mui-error": fieldErrorTextSx,
      },
    },
  },
  MuiChip: {
    variants: [
      {
        props: { variant: "featureTag" },
        style: ({ theme }) => ({
          height: 26,
          minHeight: 26,
          borderRadius: 6,
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.mode === "light" ? theme.palette.grey[400] : theme.palette.grey[700]}`,
          "& .MuiChip-label": {
            paddingInline: 11,
            fontSize: 12,
            fontWeight: 600,
          },
        }),
      },
    ],
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => outlinedInputRoot({ theme }),
      input: ({ theme, ownerState }) => outlinedInputInput({ theme, size: ownerState.size }),
    },
  },
  MuiInputLabel: {
    // Re-center the resting label inside our globally shrunken outlined inputs
    // (28px small / 36px medium). MUI's default resting transforms assume the
    // stock ~40px/~56px heights; without this override the label-as-placeholder
    // sits visually below center. Only applies when NOT shrunk so the
    // float-to-top behavior on focus / when a value is present uses MUI's
    // default shrunk transform.
    styleOverrides: {
      root: ({ ownerState }) => {
        if (ownerState.variant !== "outlined" || ownerState.shrink) {
          return {};
        }
        return {
          transform:
            ownerState.size === "small"
              ? "translate(14px, 2px) scale(1)"
              : "translate(14px, 6px) scale(1)",
        };
      },
    },
  },
  // MUI X v9 renamed the day slot to `MuiPickerDay` (singular). Its default
  // "today" style is an `outline` over an explicit `backgroundColor: transparent`
  // base, so the current date reads as an empty ring. Fill it with the brand
  // primary (light: #1447E6) + contrast text so today is a solid chip. The
  // `today` class is only present when today is NOT the selected day, so the
  // selected-day style still wins when they coincide.
  MuiPickerDay: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.MuiPickerDay-today": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          outline: "none",
          "&:hover, &:focus, &.Mui-focusVisible": {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
          },
        },
      }),
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      // Pickers expose the field size as `inputSize` on their ownerState.
      root: ({ theme, ownerState }) => ({
        ...outlinedInputRoot({ theme }),
        height: inputHeight(ownerState.inputSize),
        fontSize: 14,
        color: theme.app.filter.inputTextColor,
        "& .MuiTypography-root": {
          fontSize: 14,
          color: theme.app.filter.inputPlaceholderColor,
        },
        "& .MuiSvgIcon-root": { fontSize: 18 },
      }),
      input: ({ theme, ownerState }) => outlinedInputInput({ theme, size: ownerState.inputSize }),
      sectionsContainer: ({ theme }) => ({
        fontSize: 14,
        color: theme.app.filter.inputTextColor,
        "& .MuiPickersSectionList-sectionContent[data-placeholder=true]": {
          color: theme.app.filter.inputPlaceholderColor,
        },
      }),
    },
  },
};

/** Structural ThemeOptions merged into every theme by `createAppTheme`. */
export const BASE_THEME_OPTIONS: ThemeOptions = {
  typography: SHARED_TYPOGRAPHY,
  shape: { borderRadius: 8 },
  components: SHARED_COMPONENTS,
};

/** Size/layout part of the `theme.app` tokens — identical across themes. */
export const BASE_APP_LAYOUT: {
  headerLogo: { fontWeight: number; fontSize: string };
  headerSearch: HeaderSearchLayout;
} = {
  headerLogo: { fontWeight: 700, fontSize: "1.25rem" },
  headerSearch: {
    widthPxSm: 320,
    widthFocusedPxSm: 450,
    transitionDurationMs: 150,
  },
};
